"use client";

import { useState } from "react";
import Link from "@/components/i18n/link";
import { slugify } from "@/lib/events";
import { useMessages } from "@/lib/i18n/client";
import messages from "./Footer.i18n";
import { useConsentStore } from "@/lib/tracking/consent";
import { currentLocale, localeHeaders } from "@/lib/i18n/request";
import { NewsletterCountriesDialog } from "@/components/newsletter/countries-dialog";
import { useEmailField } from "@/lib/email-field";
import { track } from "@/lib/tracking/track";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001/api";

export function Footer() {
  const t = useMessages(messages);
  const reopenConsent = useConsentStore((s) => s.reopen);
  return (
    <footer className="mt-24 border-t border-border bg-surface">
      <div className="container-page py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2">
              {/* SVG local: el optimizador de next/image no aporta nada acá. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo.svg" alt="Ciudad Viva" className="h-8 w-auto" />
              <span className="text-lg font-semibold tracking-tight">Ciudad Viva</span>
            </div>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">{t.tagline}</p>
          </div>

          <FooterCol title={t.explore} links={[
            { label: t.allEvents, href: "/eventos" },
            { label: t.organizations, href: "/organizaciones" },
            { label: t.freeEvents, href: "/eventos/categoria/gratis" },
            { label: t.musicEvents, href: `/eventos/categoria/${slugify("Música")}` },
            { label: t.cultureEvents, href: `/eventos/categoria/${slugify("Cultura")}` },
            { label: t.familyEvents, href: `/eventos/categoria/${slugify("Familia")}` },
          ]} />

          <FooterCol title={t.company} links={[
            { label: t.howItWorks, href: "/como-funciona" },
            { label: t.suggestions, href: "/sugerencias" },
            { label: t.contact, href: "/contacto" },
            { label: t.terms, href: "/terminos" },
            { label: t.privacy, href: "/privacidad" },
            { label: t.dataDeletion, href: "/eliminar-datos" },
          ]}>
            <li>
              <button
                type="button"
                onClick={reopenConsent}
                className="text-left hover:cursor-pointer hover:text-foreground"
              >
                {t.cookiePreferences}
              </button>
            </li>
          </FooterCol>
        </div>

        <FooterNewsletter />

        <div className="mt-12 flex flex-col gap-4 border-t border-border pt-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <p>{t.copyright(new Date().getFullYear())}</p>
        </div>
      </div>
    </footer>
  );
}

function FooterNewsletter() {
  const t = useMessages(messages);
  const { email, rawEmail, setEmail, error: emailError, validate } = useEmailField();
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [picking, setPicking] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) setPicking(true);
  };

  const subscribe = async (countries: string[]) => {
    setPicking(false);
    setStatus("loading");
    try {
      const res = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...localeHeaders() },
        body: JSON.stringify({ email, language: currentLocale(), countries }),
      });
      if (!res.ok) throw new Error();
      setStatus("done");
      track("newsletter_subscribe", { source: "footer" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="mb-10 mt-12 border-t border-border pt-10">
      {picking && <NewsletterCountriesDialog onConfirm={subscribe} onCancel={() => setPicking(false)} />}
      <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">Newsletter</p>
      <h4 className="text-sm font-semibold text-foreground mb-1">{t.newsletterTitle}</h4>
      {status === "done" ? (
        <p className="text-sm text-muted-foreground">{t.newsletterDone}</p>
      ) : (
        <form noValidate onSubmit={handleSubmit} className="mt-3 flex gap-2 max-w-sm">
          <input
            type="email"
            placeholder={t.emailPlaceholder}
            value={rawEmail}
            onChange={(e) => setEmail(e.target.value)}
            aria-label={t.emailPlaceholder}
            aria-invalid={!!emailError}
            className={`flex-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary ${emailError ? "border-destructive" : "border-border"}`}
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
          >
            {status === "loading" ? "..." : t.subscribe}
          </button>
        </form>
      )}
      {emailError && <p className="mt-1 text-xs text-destructive">{emailError}</p>}
      {status === "error" && (
        <p className="mt-1 text-xs text-destructive">{t.subscribeError}</p>
      )}
    </div>
  );
}

function FooterCol({
  title,
  links,
  children,
}: {
  title: string;
  links: { label: string; href: string }[];
  children?: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
        {links.map((l) => (
          <li key={l.href}>
            <Link href={l.href} className="hover:text-foreground">{l.label}</Link>
          </li>
        ))}
        {children}
      </ul>
    </div>
  );
}
